import { useState } from "react";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { X, Sparkles } from "lucide-react";

const OPTIONS = [
  { v: "submitted", label: "Received" },
  { v: "under_review", label: "Under review" },
  { v: "approved", label: "Approved" },
  { v: "rejected", label: "Rejected" },
  { v: "paid", label: "Paid" },
  { v: "duplicate", label: "Duplicate" },
];

export default function CustomStatusModal({ submission, onClose, onSaved }) {
  const [newStatus, setNewStatus] = useState(submission.status);
  const [note, setNote] = useState("");
  const [finalPayout, setFinalPayout] = useState(submission.final_payout || submission.estimated_payout || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/submissions/${submission.id}/status`, {
        status: newStatus,
        note,
        final_payout: newStatus === "paid" ? Number(finalPayout) : undefined,
      });
      toast.success(`Status → ${newStatus.replace("_", " ")}`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="custom-status-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center"><Sparkles size={15} /></div>
            <div>
              <h3 className="font-black text-slate-900">Custom status update</h3>
              <p className="text-xs text-slate-500 font-mono">{submission.reference}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-900 hover:text-white flex items-center justify-center"><X size={15} /></button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">New status</label>
            <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} data-testid="custom-status-select"
              className="mt-1.5 w-full h-11 px-3 rounded-lg border border-slate-200 bg-white text-sm font-bold text-slate-800 focus:outline-none focus:border-indigo-400">
              {OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </div>

          {newStatus === "paid" && (
            <div>
              <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Final payout ($)</label>
              <input type="number" step="0.01" value={finalPayout} onChange={(e) => setFinalPayout(e.target.value)} data-testid="custom-final-payout"
                className="mt-1.5 w-full h-11 px-3 rounded-lg border border-slate-200 text-sm font-mono focus:outline-none focus:border-indigo-400" />
            </div>
          )}

          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-slate-500">Internal note (customer sees status only)</label>
            <textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add context for the audit log & timeline…" data-testid="custom-note"
              className="mt-1.5 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none focus:outline-none focus:border-indigo-400" />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="px-4 h-10 rounded-lg text-sm font-bold text-slate-600 hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="custom-save"
            className="px-5 h-10 rounded-lg bg-gradient-to-r from-indigo-500 to-indigo-700 hover:from-indigo-600 hover:to-indigo-800 text-white text-sm font-black disabled:opacity-50">
            {busy ? "Saving…" : "Save update"}
          </button>
        </div>
      </div>
    </div>
  );
}
